import React from "react";

// The Control Room's three tabs. A first visit opens on Context, because setting BossDrop
// up starts with its tools and team. After that each person's last tab is kept in this
// browser.
export const CONTROL_ROOM_TABS = [
  { key: "context", label: "Context", icon: "context" },
  { key: "automations", label: "Automations", icon: "automations" },
  { key: "tuning", label: "Fine tuning", icon: "tuning" },
];

const KEY = "bd:controlRoomTab";
export const CONTROL_ROOM_TAB_EVENT = "bd:control-room-tab";

export function readControlRoomTab() {
  try {
    const value = localStorage.getItem(KEY);
    return CONTROL_ROOM_TABS.some((t) => t.key === value) ? value : CONTROL_ROOM_TABS[0].key;
  } catch {
    return CONTROL_ROOM_TABS[0].key;
  }
}

export function saveControlRoomTab(tab) {
  try {
    localStorage.setItem(KEY, tab);
  } catch {}
}

// For a link on another tab: this one on screen now, so the caller can scroll within it.
export function showControlRoomTab(tab) {
  window.dispatchEvent(new CustomEvent(CONTROL_ROOM_TAB_EVENT, { detail: { tab } }));
}

// Every tab stays mounted, only hidden, so what a card read stands until it reads again.
// This re-reads when someone comes back to the hub, so a pipeline made or a tool connected
// in another browser tab shows up without a reload. Only while `tab` is on screen (null when
// `enabled` already says so), and at most once per gapMs, because reading a tool's list
// spends that tool's API calls.
const LIST_REFRESH_GAP_MS = 15 * 1000;

export function useRefreshOnReturn(refresh, enabled, { tab = "context", gapMs = LIST_REFRESH_GAP_MS } = {}) {
  const latest = React.useRef(refresh);
  React.useEffect(() => {
    latest.current = refresh;
  });
  React.useEffect(() => {
    if (!enabled) return undefined;
    let last = 0;
    const onReturn = () => {
      if (document.visibilityState === "hidden") return;
      if (tab && readControlRoomTab() !== tab) return;
      if (Date.now() - last < gapMs) return;
      last = Date.now();
      latest.current();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [enabled, tab, gapMs]);
}
